import { useState } from 'react'
import { Package, Search, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Hit {
  ecosystem: string
  name: string
  latest_version: string
  first_published: string
  author: string
  url: string
  age_days: number | null
  suspicious: boolean
}

interface Result {
  ecosystem: string
  watched: string
  variants_checked: number
  variants_published: number
  hits: Hit[]
}

export default function DepConfusionPage() {
  const [ecosystem, setEcosystem] = useState<'npm' | 'pypi'>('npm')
  const [pkg, setPkg] = useState('')
  const [data, setData] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scan = async () => {
    if (!pkg.trim()) return
    setLoading(true); setError(null); setData(null)
    try {
      const r = await api.get('/api/sec/dep-confusion', {
        params: { ecosystem, name: pkg.trim(), max_variants: 60 },
      })
      setData(r.data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Scan failed')
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Package size={24} className="text-amber-light" /> Dependency Confusion Monitor
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Find npm / PyPI packages whose names typo-squat your internal libs. Public registries, no auth.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">Ecosystem</span>
            <select value={ecosystem} onChange={e => setEcosystem(e.target.value as 'npm' | 'pypi')}
              className="input py-1.5 text-xs">
              <option value="npm">npm</option>
              <option value="pypi">PyPI</option>
            </select>
          </label>
          <label className="space-y-1 flex-1 min-w-[260px]">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">Internal package name</span>
            <input value={pkg} onChange={e => setPkg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && scan()}
              placeholder='e.g. "boe-internal-utils"'
              className="input w-full py-1.5 text-xs" />
          </label>
          <button type="button" onClick={scan} disabled={loading || !pkg.trim()}
            className="btn-primary text-xs">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
            Scan variants
          </button>
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          Generates ~60 confusion variants (char-swap, drop, repeat, hyphen-swap, homoglyph, suspicious affixes)
          and queries the public registry for each.
        </p>
      </div>

      {error && <div className="card p-3 bg-rose/10 border border-rose/30 text-xs text-rose-light">{error}</div>}

      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Variants checked"  value={data.variants_checked} />
            <Stat label="Published lookalikes" value={data.variants_published} tone={data.variants_published > 0 ? 'amber' : 'emerald'} />
            <Stat label="Suspicious (<90d)" value={data.hits.filter(h => h.suspicious).length} tone={data.hits.filter(h => h.suspicious).length > 0 ? 'rose' : 'emerald'} />
          </div>

          {data.hits.length === 0 ? (
            <div className="card p-6 text-center text-text-muted text-xs italic">
              No confusion variants of <code className="font-mono">{data.watched}</code> found on {data.ecosystem}. Clean.
            </div>
          ) : (
            <div className="card p-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                    <th className="text-left py-2 px-2">Variant</th>
                    <th className="text-left py-2 px-2">Version</th>
                    <th className="text-left py-2 px-2">Published</th>
                    <th className="text-left py-2 px-2">Author</th>
                    <th className="text-right py-2 px-2">Age</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.hits.map(h => (
                    <tr key={h.name} className={clsx('border-b border-border/50 hover:bg-surface-hover',
                      h.suspicious && 'bg-rose/5')}>
                      <td className="py-1.5 px-2 font-mono text-blue-light">
                        {h.suspicious && <AlertTriangle size={10} className="inline text-rose mr-1" />}
                        {h.name}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{h.latest_version || '—'}</td>
                      <td className="py-1.5 px-2 text-[10px] text-text-muted">{h.first_published?.slice(0, 10) || '—'}</td>
                      <td className="py-1.5 px-2 text-text-secondary truncate max-w-[160px]">{h.author}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-[10px]">
                        {h.age_days != null ? `${h.age_days}d` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        <a href={h.url} target="_blank" rel="noreferrer" className="text-blue-light hover:underline inline-flex items-center gap-1 text-[10px]">
                          open <ExternalLink size={9} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'amber' | 'rose' | 'emerald' }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-text-primary'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t)}>{value}</div>
    </div>
  )
}
